import { useState, useCallback } from 'react';
import { useEventBus } from '@/hooks/use-event-bus';
import { PlaceDto } from '@/api/place';
import api from '@/api';

// Event name for place like status changes
export const PLACE_LIKE_EVENT = 'place-like-status-change';

// Event payload type
export interface PlaceLikeEvent {
  placeId: number;
  isLiked: boolean;
  likeCount?: number;
}

/**
 * Hook for handling place likes
 */
export const usePlaceLike = (place?: PlaceDto | null) => {
  const [likeTimeout, setLikeTimeout] = useState<NodeJS.Timeout>();

  // Initialize event bus for like status changes
  const { emit } = useEventBus<PlaceLikeEvent>(PLACE_LIKE_EVENT);

  const handleLike = useCallback((isLiked: boolean) => {
    if(!place) return;
    if(!place.id) return;

    if(likeTimeout) {
      clearTimeout(likeTimeout);
    }

    setLikeTimeout(setTimeout(async () => {
      try {
        if(!place) return;
        if(!place.id) return;
        if (isLiked) {
          await api.placeLike.like(place.id);
        } else {
          await api.placeLike.unlike(place.id);
        }

        // Emit event to notify other components about the like status change
        emit({
          placeId: place.id,
          isLiked: isLiked,
          likeCount: isLiked ? (place.likeCount || 0) + 1 : Math.max(0, (place.likeCount || 0) - 1)
        });
      } catch (error) {
        console.error('Error toggling place like:', error);
      }
      setLikeTimeout(undefined);
    }, 300));
  }, [place?.id, likeTimeout, emit]);

  return {
    handleLike,
  };
};